import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { Reveal, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYEE_RANGES = ["1–10", "11–25", "26–50", "51–100", "101–250", "250+"];

const SERVICE_OPTIONS = [
  "Manager Development",
  "Workplace Skills Labs",
  "HR Consulting",
  "Operations Consulting",
  "Not sure yet",
];

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  employees: "",
  services: [],
  message: "",
};

const inputCls =
  "w-full bg-transparent border-b border-ink/15 focus:border-terracotta outline-none py-3 text-base text-ink placeholder:text-ink/30 transition-colors duration-300";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs tracking-[0.18em] uppercase font-bold text-ink-soft mb-1">
      {label}
    </label>
    {children}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s)
        ? f.services.filter((x) => x !== s)
        : [...f.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(initialForm);
      toast.success("Thank you. Your enquiry is in, and we'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-ink text-white py-24 md:py-32 px-6 md:px-10 rounded-t-[2.5rem] md:rounded-t-[4rem]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-24">
        <div>
          <Reveal>
            <Overline>Let's Talk</Overline>
            <h2 className="mt-6 font-display font-bold tracking-tight text-4xl md:text-6xl leading-[1.05]">
              Let's build a{" "}
              <span className="font-serif italic font-medium text-terracotta-light">better workplace.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-white/70 max-w-md">
              Whether you're overwhelmed by people challenges, outgrowing your
              operations, or ready to develop your leaders, the first step is a
              conversation. Schedule a consultation and we'll walk through where
              you are and where you want to be.
            </p>
            <ul className="mt-10 space-y-4">
              {[
                "A real conversation, not a sales pitch",
                "Honest assessment of where we can help",
                "Clear next steps, whether we work together or not",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/80 text-sm md:text-base">
                  <CheckCircle size={20} weight="fill" className="text-terracotta-light shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-white text-ink rounded-3xl p-7 md:p-10 space-y-7 shadow-[0_24px_60px_rgb(0,0,0,0.25)]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
              <Field label="Name *" htmlFor="contact-name">
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </Field>
              <Field label="Company *" htmlFor="contact-company">
                <input
                  id="contact-company"
                  data-testid="contact-company-input"
                  required
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Company name"
                  className={inputCls}
                />
              </Field>
              <Field label="Email *" htmlFor="contact-email">
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </Field>
              <Field label="Phone" htmlFor="contact-phone">
                <input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(555) 000-0000"
                  className={inputCls}
                />
              </Field>
            </div>

            <Field label="Number of Employees" htmlFor="contact-employees">
              <select
                id="contact-employees"
                data-testid="contact-employees-select"
                value={form.employees}
                onChange={set("employees")}
                className={`${inputCls} cursor-pointer ${form.employees ? "" : "text-ink/30"}`}
              >
                <option value="" disabled>Select a range</option>
                {EMPLOYEE_RANGES.map((r) => (
                  <option key={r} value={r}>{r} employees</option>
                ))}
              </select>
            </Field>

            <div>
              <p className="text-xs tracking-[0.18em] uppercase font-bold text-ink-soft mb-3">
                What services are you interested in?
              </p>
              <div className="flex flex-wrap gap-2.5">
                {SERVICE_OPTIONS.map((s) => {
                  const active = form.services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => toggleService(s)}
                      aria-pressed={active}
                      className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors duration-300 ${
                        active
                          ? "bg-terracotta border-terracotta text-white"
                          : "border-ink/15 text-ink-soft hover:border-terracotta hover:text-terracotta"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <Field label="Message *" htmlFor="contact-message">
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what's going on in your business: the challenges, the goals, the stuck points."
                className={`${inputCls} resize-none`}
              />
            </Field>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="w-full rounded-full bg-ink text-white font-semibold px-8 py-4 text-base flex items-center justify-center gap-2 hover:bg-terracotta hover:scale-[1.02] transition-[background-color,transform] duration-300 disabled:opacity-60 disabled:hover:scale-100"
            >
              {sending ? (
                <>
                  <CircleNotch size={20} weight="bold" className="animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Schedule a Consultation
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </button>
            {sent && (
              <p data-testid="contact-success-message" className="text-center text-sm text-terracotta font-semibold">
                Enquiry received. We'll be in touch within one business day.
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
